import { skipToken, useQuery } from '@tanstack/react-query';
import { Phone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router';
import type { ProductDetail } from '@sbg/shared/schemas';
import { productWhatsAppMessage, whatsappLink } from '@sbg/shared/utils';
import { WhatsAppButton } from '@/components/product/WhatsAppButton';
import { AppLink } from '@/components/ui/AppLink';
import { buttonClasses } from '@/components/ui/button-classes';
import { WhatsAppGlyph } from '@/components/ui/glyphs';
import { track } from '@/lib/analytics';
import { queryKeys } from '@/lib/api/keys';
import { absoluteUrl } from '@/lib/config';
import { useT } from '@/lib/i18n';
import { usePublicSettings } from '@/lib/site/public-settings';
import { scrollStore } from '@/motion/scroll';

/** Pages where a floating contact shortcut would only get in the way (account, auth, admin, the form itself). */
const QUIET_PREFIXES = [
  '/admin',
  '/account',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/contact-us',
  '/design',
  '/preview',
];

/** Shown after one screen of scrolling; hidden again in the last 400 px so it never covers the footer. */
const END_ZONE_PX = 400;

function useDockVisible(): boolean {
  const [visible, setVisible] = useState(false);
  useEffect(
    () =>
      scrollStore.subscribe((scroll) => {
        const past = scroll.y > scroll.viewportHeight;
        const remaining = document.documentElement.scrollHeight - scroll.y - scroll.viewportHeight;
        setVisible(past && remaining > END_ZONE_PX);
      }),
    [],
  );
  return visible;
}

/**
 * The CTA dock (DESIGN_SYSTEM §4.7): a quiet shortcut to the two things most visitors want — ask a
 * question, or chat. Hidden on the first screen; after one screen of scrolling it appears bottom-right
 * (desktop: a WhatsApp button and a "Request a quote" pill) or as a bottom bar (phones: Call ·
 * WhatsApp · Enquiry, 44 px targets, safe-area aware). It stays out of the way: not on account, auth,
 * admin or contact pages, not over the footer, not while a dialog or the consent bar is open. On a
 * product page it carries that stone into the enquiry and the chat.
 */
export function CtaDock() {
  const { pathname } = useLocation();
  const t = useT();
  const settings = usePublicSettings();
  const visible = useDockVisible();

  // On a product page: the stone the visitor is looking at (read from the cache, never fetched here).
  const slug = matchPath('/shop/:slug', pathname)?.params.slug;
  const product = useQuery<ProductDetail>({
    queryKey: queryKeys.catalog.product(slug ?? ''),
    queryFn: skipToken,
  }).data;

  if (
    !visible ||
    QUIET_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
  ) {
    return null;
  }

  const enquiry = {
    pathname: '/contact-us',
    ...(slug ? { search: `?product=${slug}` } : {}),
    hash: '#enquiry',
  };
  const phoneHref = settings.phone ? `tel:${settings.phone.replace(/[^\d+]/g, '')}` : null;
  const chat =
    settings.whatsappNumber && product
      ? whatsappLink(
          settings.whatsappNumber,
          productWhatsAppMessage({
            name: product.name,
            sku: product.sku,
            url: absoluteUrl(`/shop/${product.slug}`),
          }),
        )
      : null;

  return (
    <nav className="cta-dock" aria-label={t('dock.label')}>
      {/* desktop: WhatsApp (round) + the quote pill */}
      <div className="cta-dock__desktop">
        {product ? (
          <WhatsAppButton
            product={{
              name: product.name,
              sku: product.sku,
              slug: product.slug,
            }}
          />
        ) : (
          <WhatsAppButton />
        )}
        <AppLink to={enquiry} className={buttonClasses({ variant: 'primary' })}>
          {t('dock.quote')}
        </AppLink>
      </div>

      {/* phones: a bar with three labelled actions */}
      <div className="cta-dock__bar">
        {phoneHref ? (
          <a className="cta-dock__action" href={phoneHref}>
            <Phone size={20} aria-hidden="true" />
            {t('dock.call')}
          </a>
        ) : null}
        {settings.whatsappNumber ? (
          <a
            className="cta-dock__action cta-dock__action--wa"
            href={chat ?? whatsappLink(settings.whatsappNumber, t('footer.whatsappGreeting'))}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              track('whatsapp_click', product ? { product: product.slug } : {}); // analytics: consent-gated
            }}
          >
            <WhatsAppGlyph size={20} />
            {t('common.whatsapp')}
            <span className="sr-only"> {t('common.opensInNewTab')}</span>
          </a>
        ) : null}
        <AppLink to={enquiry} className="cta-dock__action cta-dock__action--primary">
          {t('common.enquiry')}
        </AppLink>
      </div>
    </nav>
  );
}
